import axios from "axios";
import { Cookies } from "react-cookie";

class ApiBase {
  constructor(baseURL) {
    this.cookies = new Cookies();

    this.client = axios.create({
      baseURL: baseURL,
      withCredentials: true,
    });

    this.isRefreshing = false;
    this.refreshSubscribers = [];

    this.client.interceptors.request.use((config) => {
      const token = this.cookies.get("auth_token");

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    this.client.interceptors.response.use(
      (response) => response,
      (error) => this.handleResponseError(error)
    );
  }

  handleResponseError = async (error) => {
    const originalRequest = error.config;

    const status = error.response?.status;

    // Handle token expiry
    if ((status === 401 || status === 440) && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const newAccessToken = await this.refreshToken();

        if (newAccessToken) {
          this.cookies.set("auth_token", newAccessToken, {
            path: "/",
            secure: process.env.NODE_ENV === "production",
            sameSite: "Strict",
          });

          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return this.client(originalRequest);
        }
      } catch (refreshError) {
        console.error("Refresh token failed:", refreshError);
        this.logout();
        window.location.href = "/login";
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  };

  refreshToken = () => {
    if (this.isRefreshing) {
      return new Promise((resolve, reject) => {
        this.refreshSubscribers.push({ resolve, reject });
      });
    }

    this.isRefreshing = true;

    return this.client
      .post("/auth/refresh")
      .then((res) => {
        const newToken = res.data.token;
        this.isRefreshing = false;
        this.onRefreshed(newToken);
        return newToken;
      })
      .catch((err) => {
        this.isRefreshing = false;
        this.onRefreshFailed(err);
        throw err;
      });
  };

  onRefreshed = (token) => {
    this.refreshSubscribers.forEach((subscriber) => {
      subscriber.resolve(token);
    });
    this.refreshSubscribers = [];
  };

  onRefreshFailed = (error) => {
    this.refreshSubscribers.forEach((subscriber) => {
      subscriber.reject(error);
    });
    this.refreshSubscribers = [];
  };

  logout() {
    this.cookies.remove("auth_token", { path: "/" });
    this.cookies.remove("user_info", { path: "/" });
  }

  get(url, params = {}) {
    return this.client.get(url, { params });
  }

  post(url, data, headers = {}) {
    const isFormData = data instanceof FormData;
    const config = {
      headers: {
        "Content-Type": isFormData ? "multipart/form-data" : "application/json",
        ...headers,
      },
    };
    return this.client.post(url, data, config);
  }

  put(url, data, headers = {}) {
    return this.client.put(url, data, { headers });
  }

  delete(url) {
    return this.client.delete(url);
  }
}

const apiBaseUrl = process.env.REACT_APP_API_URL || "http://localhost:3000";
export default new ApiBase(apiBaseUrl);
